// SurfaceGuide - K853 "where do I do X", K855 made it walk, K857 made it check.
//
// WHAT IS GENERATED
//   Which controls, in what order, AND the sentence for each step. The model
//   is handed the exact list of controls that exist on the rendered page and
//   writes to that list; an id it was not given is discarded, and the sentence
//   is sanitised (no markup, length-capped) with the templated wording as a
//   fallback. Templating every sentence was caution on my part rather than a
//   requirement - given the real controls, the model writes something more
//   useful than "Click X".
//
// WHAT IS NOT, AND CANNOT BE
//   Whether anything actually happened. That is a fact about the server and
//   the page, not something a model can know by being clever, and "saved!"
//   when nothing saved is the single failure that would discredit a product
//   selling verified claims. So every step is CHECKED rather than assumed:
//   the page is fingerprinted before the action and compared after, and the
//   walkthrough reports what it can actually see - including "I cannot see
//   anything that changed", which is a real answer, not a missing one.
//
//   When the application says something itself - a role=alert or role=status
//   - that text is quoted verbatim. The product's own words beat any summary
//   of them, and an error is surfaced as a failure rather than swept past.
//
// WHAT THIS STILL IS NOT
//   An authored guide re-queries the server and can say "the rule is stored".
//   This reads the page. A visible change is strong evidence and no change is
//   honest doubt, but neither is a server read-back, and the wording never
//   pretends otherwise.
//
// DEGRADING, IN RUNGS
//   model orders >= 2 real controls -> a checked walkthrough
//   fewer, or no model              -> the single "here is the page" card
//   sidebar collapsed / no anchor   -> that card with no highlight, saying so
//   Each rung works with the one above unavailable.

import { useEffect, useRef, useState, useCallback } from 'react';
import { createHighlighter } from '../lib/walkthrough/highlight.js';
import { cssEscape } from '../lib/walkthrough/resolve.js';
import {
  collectAnchors, anchorCatalogue, parseSteps, pageFingerprint, describeChange,
} from '../lib/walkthrough/page-anchors.js';

const SETTLE_MS = 450;
/* Clicking a control reveals things that did not exist when the page was
   first read, so a later step re-resolves and waits rather than giving up. */
const RESOLVE_MS = 2500;
/* G360_K880 - how long to wait for the PAGE to exist before reading its
   controls. Every step already waits up to RESOLVE_MS for its own target
   with a MutationObserver; the initial collection waited a flat SETTLE_MS
   and took whatever was there. On a route that is lazy-loaded through
   Suspense and then fetches its data, 450ms is a race. */
const ANCHOR_BUDGET_MS = 4000;
const ANCHOR_POLL_MS = 150;
/* A save is a round trip. Checking the instant the click lands would read the
   page before the application has answered, and report "nothing changed" for
   something that worked. */
const SETTLE_AFTER_ACTION_MS = 900;
/* K858 - a reload has to actually refetch. Leaving and returning remounts the
   lazy route component, so its data comes from the server rather than from
   whatever React was still holding. */
const RELOAD_AWAY_MS = 350;
const RELOAD_BACK_MS = 1200;

function find(testid) {
  try {
    const all = document.querySelectorAll('[data-testid="' + cssEscape(testid) + '"]');
    if (all.length !== 1) return null;          // absent or ambiguous: both are a stop
    const el = all[0];
    const r = el.getBoundingClientRect();
    return (r.width > 0 || r.height > 0) ? el : null;
  } catch { return null; }
}

function waitFor(testid, ms) {
  return new Promise((resolve) => {
    const got = find(testid);
    if (got) { resolve(got); return; }
    let done = false;
    const stop = (v) => { if (done) return; done = true; obs.disconnect(); clearTimeout(t); resolve(v); };
    const obs = new MutationObserver(() => { const el = find(testid); if (el) stop(el); });
    obs.observe(document.body, { childList: true, subtree: true, attributes: true });
    const t = setTimeout(() => stop(null), ms);
  });
}

/* G360_K880 - wait until the page actually has controls, then read them.

   ConsoleApp wraps every route in <Suspense fallback={<div
   data-testid="page-loading">}>, so for the first few hundred ms .ap-page
   contains one div and nothing else - and a div is not an anchor, so the
   catalogue came back empty and the walkthrough reported "fewer than two
   controls on this page" about a page full of them.

   This race existed before K879 and was invisible: the document-wide walk
   always found 40 sidebar links, which render immediately. Scoping to the
   page removed the thing that was accidentally hiding it.

   Keeps the best reading rather than the last, so a page that briefly
   renders controls and then swaps them is not read at its emptiest. */
async function settleAnchors(budgetMs) {
  const deadline = Date.now() + budgetMs;
  let best = collectAnchors();
  while (best.length < 2 && Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, ANCHOR_POLL_MS));
    const now = collectAnchors();
    if (now.length > best.length) best = now;
  }
  return best;
}

export default function SurfaceGuide({ surface, question, onNavigate, onEnd, ask }) {
  const hlRef = useRef(null);
  const stepsRef = useRef([]);
  /* The page as it was BEFORE the walkthrough touched anything. A change that
     survives a reload and is still absent from this baseline is the server's,
     not React's. */
  const baselineRef = useRef(null);
  const markRef = useRef(null);          // fingerprint taken when a step opened
  const [idx, setIdx] = useState(-1);
  const [result, setResult] = useState(null);
  const [finished, setFinished] = useState(false);
  const [note, setNote] = useState('');

  const end = useCallback((why) => {
    if (hlRef.current) hlRef.current.hide();
    if (onEnd) onEnd(why);
  }, [onEnd]);

  /* Compare the page against the mark taken when this step opened. */
  const check = useCallback(async () => {
    await new Promise((r) => setTimeout(r, SETTLE_AFTER_ACTION_MS));
    const before = markRef.current;
    if (!before) return null;
    try { return describeChange(before, pageFingerprint()); } catch { return null; }
  }, []);

  /* THE SERVER READ-BACK, and the reason the generated tier is no longer
     limited to "what the page shows".
     Comparing the DOM before and after an action proves React changed; it
     does not prove anything was stored. Leaving the route and coming back
     remounts the page, so what renders is what the server returned. If the
     change is still there against the baseline taken before the walkthrough
     began, it survived a round trip - that IS server evidence, and it works
     for any page without an authored predicate.
     It is offered rather than automatic: navigating away is disruptive, and
     doing it to someone without asking would be the wrong trade. */
  const confirmFromServer = useCallback(async () => {
    const hl = hlRef.current;
    if (!hl || !surface || typeof onNavigate !== 'function') return;
    hl.show({
      element: null, title: 'Govern360 Guide',
      body: 'Reloading ' + surface.label + ' to see whether the change came back from the server\u2026',
      actions: [],
    });
    setNote('Checking with the server.');
    try {
      onNavigate('dashboard');
      await new Promise((r) => setTimeout(r, RELOAD_AWAY_MS));
      onNavigate(surface.route);
      await new Promise((r) => setTimeout(r, RELOAD_BACK_MS));
    } catch { /* fall through to whatever the page shows */ }

    const d = describeChange(baselineRef.current || pageFingerprint(), pageFingerprint());
    const body = d.ok === true
      ? 'Confirmed. After reloading the page from the server, ' + d.text.charAt(0).toLowerCase() + d.text.slice(1)
        + ' It survived a round trip, so it is stored and not just on screen.'
      : d.ok === false
        ? d.text + ' That came back after a reload, so it is the stored state, not a transient error.'
        : 'After reloading, the page looks the way it did before we started. '
          + 'I cannot find evidence the change was saved - check the form for an error, or try the last step again.';
    setNote(body);
    hl.show({
      element: null, title: 'Govern360 Guide', body,
      actions: [{ label: 'Close', describe: 'Close the guide', onClick: () => end('completed') }],
      onDismiss: () => end('dismissed'),
    });
  }, [surface, onNavigate, end]);

  // --- the closing verdict ------------------------------------------------
  const finish = useCallback(async () => {
    const d = await check();
    const hl = hlRef.current;
    if (!hl) return;
    const body = !d || d.ok === null
      ? 'The steps are done, but I cannot see anything on the page that changed. '
        + 'If you expected something to be saved, check for an error on the form, or refresh and look again.'
      : d.ok === false
        ? d.text + ' The last step did not complete.'
        : 'Done. ' + d.text + ' That is what the page shows - I have not re-read it from the server.';
    setResult(d); setFinished(true); setNote(body);
    hl.show({
      element: null, title: 'Govern360 Guide', body,
      actions: [
        /* Offered whatever the page said. A visible change still might not
           have been stored, and "no change" might just be a list that does
           not refresh itself - both are worth settling. */
        { label: 'Confirm from the server', describe: 'Reload the page and check the change persisted',
          onClick: () => { confirmFromServer(); } },
        { label: 'Close', describe: 'Close the guide', onClick: () => end('completed') },
      ],
      onDismiss: () => end('dismissed'),
    });
  }, [check, end, confirmFromServer]);

  // --- paint one step -----------------------------------------------------
  const paint = useCallback(async (i) => {
    const hl = hlRef.current;
    const steps = stepsRef.current;
    if (!hl || !steps.length || i < 0 || i >= steps.length) return;
    const a = steps[i];
    const el = await waitFor(a.testid, RESOLVE_MS);
    const last = i === steps.length - 1;

    /* A step whose control never appeared is stated, not skipped silently.
       Skipping renumbers the walkthrough and quietly drops something the
       person was told they would be shown. */
    const instruction = el
      ? a.say
      : '"' + a.name + '" is not on screen at the moment - it may appear once an earlier '
        + 'step is done, or it may not apply to this organisation.';
    /* The previous step's outcome leads, so the person sees whether what they
       just did landed before being asked to do the next thing. */
    const body = (result && result.text ? result.text + '\n\n' : '') + instruction;

    markRef.current = pageFingerprint();

    const advance = async () => { const d = await check(); setResult(d); setIdx(i + 1); };

    hl.show({
      element: el,
      title: 'Govern360 Guide',
      body,
      stepNumber: i + 1,
      stepTotal: steps.length,
      onDismiss: () => end('dismissed'),
      actions: [
        last
          ? { label: 'Check it worked', describe: 'Check what changed on the page', onClick: () => { finish(); } }
          : { label: 'Next', describe: 'Go to the next step', onClick: () => { advance(); } },
        { label: 'Stop', describe: 'Stop the walkthrough', onClick: () => end('cancelled') },
      ],
    });
    setNote('Step ' + (i + 1) + ' of ' + steps.length + '. ' + body);

    /* Clicking the thing you were just told to click should move you on - but
       only where a click IS the action. An input or a select is not finished
       by being clicked, so those wait for Next. */
    if (el && (a.kind === 'button' || a.kind === 'tab' || a.kind === 'link')) {
      const go = () => { el.removeEventListener('click', go); if (last) finish(); else advance(); };
      el.addEventListener('click', go, { once: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check, end, finish, result]);

  useEffect(() => { if (idx >= 0 && !finished) paint(idx); }, [idx, finished]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- start --------------------------------------------------------------
  useEffect(() => {
    if (!surface) return undefined;
    let dead = false;
    const hl = createHighlighter();
    hlRef.current = hl;

    (async () => {
      if (typeof onNavigate === 'function') onNavigate(surface.route);
      await new Promise((r) => setTimeout(r, SETTLE_MS));
      if (dead) return;

      const anchors = await settleAnchors(ANCHOR_BUDGET_MS);
      if (dead) return;
      /* The baseline is taken AFTER settling, deliberately. Fingerprinting a
         half-rendered page makes the rest of the page load look like the
         change the walkthrough caused, and "Confirm from the server" would
         then report the page appearing as evidence the save worked. */
      baselineRef.current = pageFingerprint();
      let steps = [];
      /* G360_K878 - WHY the walkthrough could not be planned.

         Five different things end up here - a page with nothing orderable
         on it, no AI key configured, a gateway error, a planner that
         declined, and a plan naming controls that are not on the page - and
         every one of them used to render the identical "this is the page"
         card with no reason attached. So "the guide stops at the first step"
         was indistinguishable across five causes, to the customer and to us;
         working out which took two rounds of console snippets when the
         product should simply have said it.

         Same family as correction 15: a signal that reports the same thing
         whether or not the work happened tells you nothing. */
      let why = null;
      if (anchors.length < 2) {
        why = 'I can see fewer than two controls on this page to put in order, so I can only show you where it is.';
      } else if (typeof ask !== 'function') {
        why = 'Step planning is not available here, so I can only show you where this page is.';
      } else {
        try {
          const res = await ask({ question: question || '', context: anchorCatalogue(anchors), mode: 'steps' });
          if (!res) {
            why = 'I got no reply when planning the steps, so I can only show you where this page is.';
          } else if (res.needsProvider) {
            /* The gateway returns this as HTTP 200, with prose in `answer`
               and NO `error` set. The old guard was `if (res && !res.error)`,
               so it sailed through and parseSteps looked for control ids in
               an apology and found none. The assistant panel shows that
               sentence; the walkthrough swallowed it. */
            why = 'No AI key is configured, so I cannot plan the steps for this page - an owner or admin can connect one under Integrations.';
          } else if (res.error) {
            why = 'I could not plan the steps for this page (' + String(res.error).slice(0, 120) + ').';
          } else {
            steps = parseSteps(res.answer, anchors);
            if (!steps.length) {
              why = /^\s*none\s*$/i.test(String(res.answer || ''))
                ? 'There is no way to do that with the controls on this page, so I can only show you where it is.'
                : 'The steps that came back did not name any control I can see on this page, so I can only show you where it is.';
            }
          }
        } catch {
          /* Guidance must never break the reply - but it must not go quiet
             either. */
          steps = [];
          why = 'Something failed while planning the steps, so I can only show you where this page is.';
        }
      }
      if (dead) return;

      /* G360_K878 - one real step IS a walkthrough. This required two, so a
         single-step plan was discarded and replaced by "this is the page" -
         strictly worse than pointing at the one control that matters. */
      if (steps.length >= 1) { stepsRef.current = steps; setIdx(0); return; }

      /* Nothing orderable, or no model: name the place. Opening a page is not
         doing the task, so this promises only a location. */
      const navEl = find('nav-' + surface.id);
      const place = navEl
        ? 'This is ' + surface.label + '. It lives under ' + surface.group
          + ' in the sidebar, highlighted here, so you can find it again.'
        : 'This is ' + surface.label + '. It lives under ' + surface.group
          + ' in the sidebar - expand the sidebar to see where.';
      /* The reason leads. Someone who asked to be walked through a task and
         got a signpost instead is owed the reason in the card, not in a
         console. */
      const body = why ? why + '\n\n' + place : place;
      hl.show({
        element: navEl, title: 'Govern360 Guide', body,
        actions: [{ label: 'Got it', describe: 'Close the guide', onClick: () => end('done') }],
        onDismiss: () => end('dismissed'),
      });
      setNote(body);
    })();

    return () => { dead = true; hl.destroy(); hlRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface]);

  /* One concise live region - progress, results and errors, not narration. */
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}
    >
      {note}
    </div>
  );
}
